import { AppToastOutlet, useAppToastRef } from './app-toast-provider';
import { restoredFragment, restoredModule } from '../general/module-history';
import { checkDependencyCycle } from '../general/dependency-cycles';
import { AuthoringIssue, ManagedModuleValidationErrors, managedModuleIssues, moduleValidationErrors } from '../general/managed-module-validation';
import { useUnsavedDraft, useUnsavedActions } from '../general/unsaved-changes-context';
import { ModuleRecoverySession, useModuleDraftRecovery } from '../general/use-module-draft-recovery';
import { flushSync } from 'react-dom';
import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useBookmarkSelection } from '../general/bookmark-selection';
import { ManagedFragmentDto, FragmentActionDto, FormFieldDto, ModuleDto, MessageDto, ThemeDto } from "../general/dtos";
import { AddEditFieldDialog } from "../components/content-creator/add-edit-field-dialog";
import { ManageFieldsPanel } from "../components/content-creator/manage-fields-panel";
import { Editor } from "@tiptap/react";
import { useApi } from "./api-provider";
import { Api } from "../general/api";
import { Toast } from "primereact/toast";
export type { ManagedModuleValidationErrors } from '../general/managed-module-validation';
export interface EditorHtmlSnapshot {
  fragmentKey: string;
  html: string;
}

interface ManagedModuleEditorContextValue {
  toastRef: React.RefObject<Toast | null>;

  restoreRevision: (snapshot: ModuleDto) => void;
  restoreFragmentRevision: (snapshot: ModuleDto, fragmentKey: string) => void;
  managedModule: ModuleDto;
  fragments: ManagedFragmentDto[];
  selectedFragmentKey: string | null;

  selectFragment: (key: string) => void;

  addFragment: () => ManagedFragmentDto;
  appendFragments: (fragments: ManagedFragmentDto[]) => void;
  updateFragment: (fragment: ManagedFragmentDto) => void;
  updateFragmentHtml: (fragmentKey: string, html: string) => void;
  deleteFragment: (fragmentKey: string) => void;
  updateFragmentsOrder: (next: ManagedFragmentDto[]) => void;

  addAction: (fragmentKey: string, action: FragmentActionDto) => void;
  updateAction: (fragmentKey: string, action: FragmentActionDto) => void;
  removeAction: (fragmentKey: string, actionKey: string) => void;
  updateActionsOrder: (fragmentKey: string, next: FragmentActionDto[]) => void;

  addFormField(fragmentKey: string, field: FormFieldDto): void;
  updateFormField(fragmentKey: string, field: FormFieldDto): void;
  removeFormField(fragmentKey: string, fieldKey: string): void;

  openFieldEditor: (field: FormFieldDto) => void;
  openNewFieldEditor: () => void;
  closeFieldEditor: () => void;
  fieldEditorVisible: boolean;
  fieldEditorMode: "add" | "edit";
  fieldBeingEdited: FormFieldDto | null;
  isFieldUsed: (fieldKey: string) => boolean;

  validationErrors: ManagedModuleValidationErrors;
  authoringIssues: AuthoringIssue[];
  validationTarget: { issue: AuthoringIssue; request: number } | null;
  focusAuthoringIssue: (issue: AuthoringIssue) => void;
  saveModuleError: string | null;
  deleteModuleError: string | null;
  updateModuleField: (field: keyof ModuleDto, value: any) => void;
  updateModuleThemes: (themes: ThemeDto[]) => void;
  updateModulePrerequisites: (moduleKeys: string[]) => void;
  validateModule: () => string[] | null;
  saveManagedModule: () => Promise<boolean>;
  registerFieldCreatedHandler: (handler: (field: FormFieldDto) => void) => void;

  fieldsPanelVisible: boolean;
  openFieldsPanel: () => void;
  closeFieldsPanel: () => void;
  toggleFieldsPanel: () => void;

  editor: Editor | null;
  setEditor: (editor: Editor | null) => void;
  registerEditorFlush: (flush: () => EditorHtmlSnapshot | null) => () => void;
  flushEditorChanges: () => void;
  clearRecovery: () => Promise<void>;
  recoveryStatus: string;

  allModules: ModuleDto[];
  allModulesLoading: boolean;
  allModulesError: string | null;
  loadAllModules: () => Promise<void>;

  themeOptions: ThemeDto[];
  themesLoading: boolean;
  loadThemes: () => Promise<void>;
}

const ManagedModuleEditorContext = createContext<ManagedModuleEditorContextValue | null>(null);

export function useManagedModuleEditor() {
  const ctx = useContext(ManagedModuleEditorContext);
  if (!ctx) throw new Error("useManagedModuleEditor must be used inside provider");
  return ctx;
}

export function ManagedModuleEditorProvider({
  initialModule,
  baselineModule = initialModule,
  recoverySession,
  children
}: {
  initialModule: ModuleDto;
  baselineModule?: ModuleDto;
  recoverySession?: ModuleRecoverySession;
  children: React.ReactNode;
}) {
  const toastRef = useAppToastRef();
  const [managedModule, setManagedModule] = useState<ModuleDto>(initialModule);
  const savedBaseline = useRef(baselineModule);
  const [selectedFragmentKey, setSelectedFragmentKey] = useBookmarkSelection('fragment');
  const [onFieldCreated, setOnFieldCreated] = useState<null | ((field: FormFieldDto) => void)>(null);
  const api: Api = useApi();
  const [validationErrors, setValidationErrors] = useState<ManagedModuleValidationErrors>({});
  const authoringIssues = useMemo(() => managedModuleIssues(managedModule), [managedModule]);
  const [validationTarget, setValidationTarget] = useState<{ issue: AuthoringIssue; request: number } | null>(null);
  const [saveModuleError, setSaveModuleError] = useState<string | null>(null);
  const [deleteModuleError, setDeleteModuleError] = useState<string | null>(null);
  const [themeOptions, setThemeOptions] = useState<ThemeDto[]>([]);
  const [themesLoading, setThemesLoading] = useState<boolean>(false);

  const registerFieldCreatedHandler = useCallback((handler: (field: FormFieldDto) => void) => {
    setOnFieldCreated(() => handler);
  }, []);

  // Field editor modal state
  const [fieldEditorVisible, setFieldEditorVisible] = useState(false);
  const [fieldEditorMode, setFieldEditorMode] = useState<"add" | "edit">("add");
  const [fieldBeingEdited, setFieldBeingEdited] = useState<FormFieldDto | null>(null);

  const [editor, setEditor] = useState<Editor | null>(null);
  const editorFlushRef = useRef<(() => EditorHtmlSnapshot | null) | null>(null);
  const registerEditorFlush = useCallback((flush: () => EditorHtmlSnapshot | null) => {
    editorFlushRef.current = flush;
    return () => {
      if (editorFlushRef.current === flush) editorFlushRef.current = null;
    };
  }, []);
  const flushEditorChanges = useCallback(() => { editorFlushRef.current?.(); }, []);
  const { flushPending } = useUnsavedActions();
  const flushRecovery = useCallback(() => {
    flushPending();
    flushSync(() => { editorFlushRef.current?.(); });
  }, [flushPending]);
  const recovery = useModuleDraftRecovery(managedModule, baselineModule, recoverySession, flushRecovery);

  const [allModules, setAllModules] = useState<ModuleDto[]>([]);
  const [allModulesLoading, setAllModulesLoading] = useState<boolean>(false);
  const [allModulesError, setAllModulesError] = useState<string | null>(null);
  const modulesInFlight = useRef<Promise<void> | null>(null);

  // Manage Fields Panel state
  const [fieldsPanelVisible, setFieldsPanelVisible] = useState(false);

  const openFieldsPanel = useCallback(() => setFieldsPanelVisible(true), []);
  const closeFieldsPanel = useCallback(() => setFieldsPanelVisible(false), []);
  const toggleFieldsPanel = useCallback(
    () => setFieldsPanelVisible(v => !v),
  []);

    // Field editor modal actions
  const openNewFieldEditor = useCallback(() => {
    setFieldBeingEdited(null);
    setFieldEditorMode("add");
    setFieldEditorVisible(true);
  }, []);

  const openFieldEditor = useCallback((field: FormFieldDto) => {
    setFieldBeingEdited(field);
    setFieldEditorMode("edit");
    setFieldEditorVisible(true);
  }, []);

  const closeFieldEditor = useCallback(() => {
    setFieldEditorVisible(false);
    setFieldBeingEdited(null);
  }, []);

  const fragments = managedModule.fragments!;

  const selectFragment = useCallback((key: string) => {
    flushEditorChanges();
    setValidationTarget(null);
    setSelectedFragmentKey(key);
  }, [flushEditorChanges, setSelectedFragmentKey]);

  const focusAuthoringIssue = (issue: AuthoringIssue) => {
    flushEditorChanges();
    if (issue.scope === 'fragment') setSelectedFragmentKey(issue.fragmentKey);
    setValidationTarget(current => ({ issue, request: (current?.request ?? 0) + 1 }));
  };

  const addFragment = useCallback((): ManagedFragmentDto => {
    flushEditorChanges();
    const newFragment: ManagedFragmentDto = {
      key: crypto.randomUUID(),
      order: fragments.length,
      title: "Untitled fragment",
      instructions: "",
      answers_explanation: null,
      aiHint: null,
      html: "",
      hidden: false,
      fillOnce: false,
      actions: [
        { key: crypto.randomUUID(), order: 1000, label: "Back", type: "gotoPrevious", to: "", payload: null},
        { key: crypto.randomUUID(), order: 2000, label: "Next", type: "gotoNext", to: "", payload: null}
      ],
      formFields: [],
    };

    setManagedModule(prev => ({
      ...prev,
      fragments: [...prev.fragments!, newFragment]
    }));

    setSelectedFragmentKey(newFragment.key);
    return newFragment;
  }, [fragments, flushEditorChanges, setSelectedFragmentKey]);

  const appendFragments = useCallback((incoming: ManagedFragmentDto[]) => {
    if (!incoming.length) return;
    const pending = editorFlushRef.current?.();
    setManagedModule(previous => {
      const existing = (previous.fragments ?? []).map(fragment => pending?.fragmentKey === fragment.key
        ? { ...fragment, html: pending.html } : fragment);
      const firstOrder = existing.reduce((highest, fragment) => Math.max(highest, fragment.order), -1) + 1;
      return { ...previous, fragments: [...existing, ...incoming.map((fragment, index) => ({ ...fragment, order: firstOrder + index }))] };
    });
    setSelectedFragmentKey(incoming[0].key);
  }, [setSelectedFragmentKey]);

  const updateFragment = useCallback((fragment: ManagedFragmentDto) => {
    setManagedModule(prev => ({
      ...prev,
      fragments: prev.fragments!.map(f => (f.key === fragment.key ? fragment : f))
    }));
  }, []);

  const updateFragmentHtml = useCallback((fragmentKey: string, html: string) => {
    setManagedModule(prev => {
      const fragment = prev.fragments?.find(f => f.key === fragmentKey);
      if (!fragment || fragment.html === html) return prev;
      return {
        ...prev,
        fragments: prev.fragments!.map(f => f.key === fragmentKey ? { ...f, html } : f),
      };
    });
  }, []);

  const deleteFragment = useCallback((fragmentKey: string) => {
    setManagedModule(prev => {
      const next = prev.fragments!.filter(f => f.key !== fragmentKey);

      // reassign order
      next.forEach((f, i) => (f.order = i));

      return { ...prev, fragments: next,
        finalQuizFragmentKey: prev.finalQuizFragmentKey === fragmentKey ? null : prev.finalQuizFragmentKey };
    });

    if (selectedFragmentKey === fragmentKey) {
      setSelectedFragmentKey(null);
    }
  }, [selectedFragmentKey, setSelectedFragmentKey]);

  const updateFragmentsOrder = useCallback((next: ManagedFragmentDto[]) => {
    // ensure order is consistent
    next.forEach((f, i) => (f.order = i));

    setManagedModule(prev => ({
      ...prev,
      fragments: [...next]
    }));
  }, []);

  const addAction = useCallback((fragmentKey: string, action: FragmentActionDto) => {
    setManagedModule(prev => ({
      ...prev,
      fragments: prev.fragments!.map(f =>
        f.key === fragmentKey
          ? { ...f, actions: [...f.actions, action] }
          : f
      )
    }));
  }, []);

  const updateAction = useCallback((fragmentKey: string, action: FragmentActionDto) => {
    setManagedModule(prev => ({
      ...prev,
      fragments: prev.fragments!.map(f =>
        f.key === fragmentKey
          ? {
              ...f,
              actions: f.actions.map(a => (a.key === action.key ? action : a))
            }
          : f
      )
    }));
  }, []);

  const removeAction = useCallback((fragmentKey: string, actionKey: string) => {
    setManagedModule(prev => ({
      ...prev,
      fragments: prev.fragments!.map(f =>
        f.key === fragmentKey
          ? { ...f, actions: f.actions.filter(a => a.key !== actionKey) }
          : f
      )
    }));
  }, []);

  const updateActionsOrder = useCallback((fragmentKey: string, next: FragmentActionDto[]) => {
    // next.forEach((a, i) => (a.order = i));

    setManagedModule(prev => ({
      ...prev,
      fragments: prev.fragments!.map(f =>
        f.key === fragmentKey ? { ...f, actions: [...next] } : f
      )
    }));
  }, []);

  const addFormField = useCallback((fragmentKey: string, field: FormFieldDto) => {
    setManagedModule(prev => ({
        ...prev,
        fragments: prev.fragments!.map(f =>
        f.key === fragmentKey
            ? { ...f, formFields: [...(f.formFields ?? []), field] }
            : f
        )
    }));
  }, []);

  const updateFormField = useCallback((fragmentKey: string, field: FormFieldDto) => {
    setManagedModule(prev => ({
        ...prev,
        fragments: prev.fragments!.map(f =>
        f.key === fragmentKey
            ? {
                ...f,
                formFields: f.formFields.map(ff =>
                ff.key === field.key ? field : ff
                )
            }
            : f
        )
    }));
  }, []);

  const removeFormField = useCallback((fragmentKey: string, fieldKey: string) => {
    setManagedModule(prev => ({
        ...prev,
        fragments: prev.fragments?.map(f =>
        f.key === fragmentKey
            ? {
                ...f,
                formFields: f.formFields.filter(ff => ff.key !== fieldKey)
            }
            : f
        )
    }));
  }, []);

  const isFieldUsed = (fieldKey: string): boolean => {
    const html = editor?.getHTML() ?? "";
    return html.includes(`key="${fieldKey}"`);
  };

  const loadAllModules = async (): Promise<void> => {
    if (modulesInFlight.current) {
        return modulesInFlight.current;
    }

    setAllModulesLoading(true);
    setAllModulesError(null);

    const promise = api.getModules(0, 10000, '', '', '', '')
    .then((data) => {
      setAllModules(data.data);
      setAllModulesLoading(false);
      modulesInFlight.current = null;
    })
    .catch((error) => {
      const message: string = error.response?.data?.message ?? 'Load error';
      setAllModulesError(message);
      setAllModulesLoading(false);
      modulesInFlight.current = null;
    });

    modulesInFlight.current = promise;
    return promise;
  };

  const updateModuleField = useCallback((field: keyof ModuleDto, value: any) => {
      setManagedModule(prev => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const updateModulePrerequisites = useCallback((moduleKeys: string[]) => {
    setManagedModule(prev => {
        if (!prev) return prev;

        return {
            ...prev,
            prerequisites: moduleKeys.map(key => ({
                module_key: key,
                is_optional: false   // default; can be edited later
            }))
        };
    });
  }, []);

  const updateModuleThemes = useCallback((themes: ThemeDto[]) => {
    setManagedModule(prev => {
        if (!prev) return prev;

        const normalized = (themes || []).map(t => {
            const val = typeof t === 'string' ? t : t.value;
            return themeOptions.find(opt => opt.value === val);
        }).filter(Boolean) as ThemeDto[];

        return {
            ...prev,
            theme: normalized
        };
    });
  }, [themeOptions]);

  const validateModule = (moduleToValidate: ModuleDto = managedModule): string[] | null => {
    const issues = managedModuleIssues(moduleToValidate);
    setValidationErrors(moduleValidationErrors(issues));
    return issues.length ? issues.map(issue => issue.message) : null;
  }

  const createModule = async (moduleToSave: ModuleDto): Promise<boolean> => {
    try {
        const created: ModuleDto | MessageDto | null = await api.createModule(moduleToSave);
        if (created && !created.hasOwnProperty('message')) {
          savedBaseline.current = created as ModuleDto;
          guard.markSaved(created);
          await recovery.clear();
          setManagedModule(created as ModuleDto);
          return true;
        } else {
            const message: string = (created && created.hasOwnProperty('message'))
                ? (created as MessageDto).message
                : 'The newly created module was not retrieved from the server propertly. Refresh the page to see if it was created.';
            setSaveModuleError(message);
            return false;
        }
    } catch (error: any) {
        const message: string = error.response?.data?.message ?? 'Failed to create module';
        setSaveModuleError(message);
        return false;
    }
  }

  const updateModule = async (moduleToSave: ModuleDto): Promise<boolean> => {
    try {
        const updated: ModuleDto | MessageDto | null = await api.updateModule(moduleToSave);
        if (updated && !updated.hasOwnProperty('message')) {
            savedBaseline.current = updated as ModuleDto;
            guard.markSaved(updated);
            await recovery.clear();
            setManagedModule(updated as ModuleDto);
            setAllModules(prev =>
                prev.map(c => c.module_key === (updated  as ModuleDto).module_key ? updated as ModuleDto : c)
            );
            return true;
        } else {
            const message: string = (updated && updated.hasOwnProperty('message'))
                ? (updated as MessageDto).message
                : 'The updated module was not retrieved from the server propertly. Refresh the page to see if it was updated properly.';
            setSaveModuleError(message);
            return false;
        }
    } catch (error: any) {
        const message: string = error.response?.data?.message ?? 'Failed to save module';
        setSaveModuleError(message);
        return false;
    }
  }

  const dependencyCheckInFlight = useRef(false);
  const saveManagedModule = async (): Promise<boolean> => {
    if (dependencyCheckInFlight.current) return false;
    const pendingHtml = editorFlushRef.current?.();
    // A flush queues React state updates. Use its snapshot directly for this save,
    // rather than waiting for a render or sending the previous fragment HTML.
    const moduleToSave = pendingHtml ? {
      ...managedModule,
      fragments: managedModule.fragments?.map(f => f.key === pendingHtml.fragmentKey ? { ...f, html: pendingHtml.html } : f),
    } : managedModule;
    const errors: string[] | null = validateModule(moduleToSave);
    toastRef?.current?.clear();
    if (errors) {
        toastRef?.current?.show({
            severity: "error",
            summary: "Validation failed",
            detail: (
                <ol style={{ marginLeft: "1rem", paddingLeft: "1rem", marginTop: "0.25rem" }}>
                    {errors.map((e, i) => (
                        <li key={i}>{e}</li>
                    ))}
                </ol>
            ),
            life: 20000
        });
        return false;
    }

    dependencyCheckInFlight.current = true;
    setSaveModuleError(null);
    try {
      const cycle = await checkDependencyCycle({ key: moduleToSave.module_key, name: moduleToSave.name,
          prerequisites: (moduleToSave.prerequisites ?? []).map(item => item.module_key) }, async key => {
              const module = await api.getModule(key);
              return module ? { key: module.module_key, name: module.name,
                  prerequisites: (module.prerequisites ?? []).map(item => item.module_key) } : null;
          });
      if (cycle) { setSaveModuleError(cycle); return false; }
      let result: boolean = false;
      if (!moduleToSave.module_key || moduleToSave.module_key.length === 0) {
          result = await createModule(moduleToSave);
      } else {
          result = await updateModule(moduleToSave);
      }

      if (result) {
          toastRef?.current?.clear();
          toastRef?.current?.show({
              severity: "success",
              summary: "Module saved",
              life: 4000
          });
      }
      return result;
    } catch (error: any) {
        setSaveModuleError(error.message ?? 'Could not verify prerequisites. Try saving again.');
        return false;
    } finally {
        dependencyCheckInFlight.current = false;
    }
  }

  useEffect(() => {
    loadAllModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fieldsPanelVisible) {
        closeFieldsPanel();
    }
  }, [selectedFragmentKey, editor === null]);

  const loadThemes = useCallback(async () => {
    setThemesLoading(true);
    try {
        const data = await api.getThemes();
        setThemeOptions(data.custom);
        // console.log(data.custom);
    } catch (error) {
        console.error("Failed to load themes", error);
    } finally {
        setThemesLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadThemes();
  }, [loadThemes]);

  const guard = useUnsavedDraft({ active: true, value: managedModule,
    baselineValue: !baselineModule.module_key && baselineModule.fragments?.length ? { ...baselineModule, fragments: [] } : baselineModule, parameter: 'module',
    save: saveManagedModule, discard: () => { void recovery.clear(); } });

  const restoreRevision = (snapshot: ModuleDto) => {
    const restored = restoredModule(snapshot, savedBaseline.current);
    recovery.resume(savedBaseline.current);
    setFieldEditorVisible(false); setFieldsPanelVisible(false); setFieldBeingEdited(null);
    setValidationErrors({}); setValidationTarget(null); setSaveModuleError(null);
    setManagedModule(restored);
    setSelectedFragmentKey(restored.fragments?.[0]?.key ?? null);
  };

  const restoreFragmentRevision = (snapshot: ModuleDto, fragmentKey: string) => {
    const pending = editorFlushRef.current?.();
    const current = pending ? { ...managedModule, fragments: managedModule.fragments?.map(fragment =>
      fragment.key === pending.fragmentKey ? { ...fragment, html: pending.html } : fragment) } : managedModule;
    try {
      const restored = restoredFragment(snapshot, current, fragmentKey);
      recovery.resume(savedBaseline.current);
      setFieldEditorVisible(false); setFieldsPanelVisible(false); setFieldBeingEdited(null);
      setValidationErrors({}); setValidationTarget(null); setSaveModuleError(null);
      setManagedModule(restored);
      setSelectedFragmentKey(fragmentKey);
    } catch (error: any) {
      setSaveModuleError(error.message ?? 'Could not restore this fragment. Your draft has not been replaced.');
    }
  };

  const value: ManagedModuleEditorContextValue = {
    restoreRevision,
    restoreFragmentRevision,
    toastRef,

    managedModule,
    fragments,
    selectedFragmentKey,

    selectFragment,

    addFragment,
    appendFragments,
    updateFragment,
    updateFragmentHtml,
    deleteFragment,
    updateFragmentsOrder,

    addAction,
    updateAction,
    removeAction,
    updateActionsOrder,

    addFormField,
    updateFormField,
    removeFormField,

    openFieldEditor,
    openNewFieldEditor,
    closeFieldEditor,
    fieldEditorVisible,
    fieldEditorMode,
    fieldBeingEdited,
    isFieldUsed,

    validationErrors,
    authoringIssues,
    validationTarget,
    focusAuthoringIssue,
    saveModuleError,
    deleteModuleError,
    updateModuleField,
    updateModuleThemes,
    updateModulePrerequisites,
    validateModule,
    saveManagedModule,
    registerFieldCreatedHandler,

    fieldsPanelVisible,
    openFieldsPanel,
    closeFieldsPanel,
    toggleFieldsPanel,
    editor,
    setEditor,
    registerEditorFlush,
    flushEditorChanges,
    clearRecovery: recovery.clear,
    recoveryStatus: recovery.status,

    allModules,
    allModulesLoading,
    allModulesError,
    loadAllModules,

    themeOptions,
    themesLoading,
    loadThemes,
  };

  return (
    <ManagedModuleEditorContext.Provider value={value}>
      {children}

      <ManageFieldsPanel />

      {fieldEditorVisible && (
        <AddEditFieldDialog
            mode={fieldEditorMode}
            field={fieldBeingEdited ?? undefined}
            onSave={(field) => {
                if (fieldEditorMode === "add") {
                    addFormField(selectedFragmentKey!, field);
                    if (onFieldCreated) {
                        onFieldCreated(field);
                    }
                } else {
                    updateFormField(selectedFragmentKey!, field);
                }
                closeFieldEditor();
            }}
            onCancel={closeFieldEditor}
        />
        )}
      <AppToastOutlet toastRef={toastRef} />
    </ManagedModuleEditorContext.Provider>
  );
}
